import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Paragraph,
  FlowGrid,
  Checkbox,
} from '@fs/zion-ui'
import { getPersonName, useSlsStateContext, getNestedOrFloatingElementById } from '@fs/zion-sls'

/**
 * PrimaryPersonDialogOverlay
 *
 * Shown when a household has no primary person. Allows user to select one or more
 * persons from the household to be designated as primary (e.g., both spouses).
 */
const PrimaryPersonDialogOverlay = ({
  overlay,
  householdMembers = [],
  currentFocusedPersonId,
  currentFocusedPersonName,
  onSave = () => {},
  onCancel = () => {},
}) => {
  const [t] = useTranslation()
  const { images } = useSlsStateContext()
  const [selectedPersonIds, setSelectedPersonIds] = useState([])
  const [error, setError] = useState('')

  // Build checkbox options from ALL household members
  // This includes the focused person AND all related persons in the household
  const personOptions = useMemo(() => {
    const options = []

    // Add the current focused person as first option
    if (currentFocusedPersonId && currentFocusedPersonName) {
      options.push({
        id: currentFocusedPersonId,
        name: currentFocusedPersonName,
      })
    }

    // Add ALL other household members (excluding focused person to avoid duplicates)
    householdMembers
      .filter((member) => {
        if (!member.isDeleted) {
          // For existing SLS persons, compare personId (unless it's NEW_PERSON)
          // For new persons, compare formId
          if (member.personId && !member.personId.includes('NEW_PERSON')) {
            return member.personId !== currentFocusedPersonId
          }
          return member.formId !== currentFocusedPersonId
        }
        return false
      })
      .forEach((member) => {
        // Check if this is a newly added person (not yet in SLS)
        if (member.personId?.includes('NEW_PERSON')) {
          // Newly added person - extract name from value.primaryText
          const displayName = member?.value?.primaryText
          if (displayName?.trim()) {
            options.push({ id: member.personId, name: displayName.trim() })
          }
        } else if (member.personId) {
          // Existing SLS person - lookup in SLS
          const person = getNestedOrFloatingElementById(images, member.personId)
          if (person) {
            const personName = getPersonName({ person, justOneName: true })
            options.push({ id: member.personId, name: personName })
          }
        } else if (member.value) {
          // Newly typed name (no personId at all) - extract from value field
          const displayName = typeof member.value === 'string' ? member.value : (member.value?.primaryText ?? '')

          const trimmedName = displayName?.trim()
          if (trimmedName) {
            options.push({ id: member.formId, name: trimmedName })
          }
        }
      })

    return options
  }, [householdMembers, currentFocusedPersonId, currentFocusedPersonName, images])

  const handleCheckboxChange = useCallback((personId) => {
    setSelectedPersonIds((prev) => {
      // Toggle: if already selected, remove; otherwise add
      if (prev.includes(personId)) {
        return prev.filter((id) => id !== personId)
      }
      return [...prev, personId]
    })
    setError('') // Clear error when selection changes
  }, [])

  const handleSave = useCallback(() => {
    // Validate selection - at least one must be selected
    if (selectedPersonIds.length === 0) {
      setError(
        t('household-details-card.primary-person-dialog.validation-error', 'Please select at least one primary person.')
      )
      return
    }

    onSave(selectedPersonIds)
    overlay.close()
  }, [selectedPersonIds, onSave, overlay, t])

  const handleCancel = useCallback(() => {
    setError('') // Clear any validation errors
    setSelectedPersonIds([]) // Reset selections
    onCancel()
    overlay.close()
  }, [onCancel, overlay])

  return (
    <DialogOverlay
      {...overlay}
      size="md"
      title={t('household-details-card.primary-person-dialog.title', 'Primary Person')}
      data-testid="primary-person-dialog-overlay"
      closeOnClickAway={false}
      nonClosable
    >
      <FlowGrid gutters="xs" orientation="vertical" columnCount={1}>
        <Paragraph size="sm">
          {t(
            'household-details-card.primary-person-dialog.description',
            'Every household requires at least one primary person. Who would you like to be set as the primary for this household?'
          )}
        </Paragraph>

        <FlowGrid gutters="xs" orientation="vertical" columnCount={1}>
          {personOptions.map((person) => (
            <Checkbox
              key={person.id}
              name={`primary-person-${person.id}`}
              label={person.name}
              checked={selectedPersonIds.includes(person.id)}
              onChange={() => handleCheckboxChange(person.id)}
              data-testid={`primary-person-checkbox-${person.id}`}
            />
          ))}
        </FlowGrid>

        {error && (
          <Alert type="warning" emphasis="high" data-testid="primary-person-error">
            {error}
          </Alert>
        )}
      </FlowGrid>

      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="low" onClick={handleCancel} data-testid="primary-person-dialog-cancel-button">
            {t('household-details-card.primary-person-dialog.cancel', 'Cancel')}
          </Button>
          <Button emphasis="high" onClick={handleSave} data-testid="primary-person-dialog-save-button">
            {t('household-details-card.primary-person-dialog.save', 'Save')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

PrimaryPersonDialogOverlay.propTypes = {
  overlay: PropTypes.shape({
    isOpen: PropTypes.bool,
    open: PropTypes.func,
    close: PropTypes.func.isRequired,
  }).isRequired,
  householdMembers: PropTypes.arrayOf(
    PropTypes.shape({
      personId: PropTypes.string, // Can be null for typed names
      formId: PropTypes.string.isRequired, // Always present, used as identifier when personId is null
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      isDeleted: PropTypes.bool,
    })
  ),
  currentFocusedPersonId: PropTypes.string,
  currentFocusedPersonName: PropTypes.string,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
}

export default PrimaryPersonDialogOverlay
